/**
 * 通行証（トークン）の置き場。
 *
 * **同じブラウザのタブどうしで分け合う**（issue #363）。localStorage に置き、ほかのタブで
 * 取り直した・ログアウトした、をすぐに受け取る。メンバーの画面を別のタブで開くたびに、
 * 電話番号と SMS の認証番号でログインし直していただかずに済むようにするため
 * （SMS は同じ番号へ1日10回までで、1通ごとに費用もかかる）。
 *
 * 共用のパソコンで開きっぱなしにしたときの備えは、しばらく操作が無いときの自動ログアウト
 * （`idle.ts`・30分）に任せる。最後に操作した時刻もタブどうしで分け合うので、どれかのタブで
 * 操作していればログインは続き、どのタブでも操作が無ければ、次に開いたときにログイン画面へ戻る。
 *
 * 保存できない設定（プライベートウィンドウ等）でも動きは止めない。その場合はメモリだけで持ち、
 * 読み込み直したときにログインからやり直していただく。
 */
import type { TokenPair } from '../api/types.js';

const STORAGE_KEY = 'field-chat.admin.session';

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  /** 最後に操作した時刻（自動ログアウトの判定に使う） */
  lastActiveAt: number;
}

/** メモリ上の控え（保存先が使えない設定のときは、これだけが頼り）。 */
let current: StoredSession | null = null;

/** ログインの結果から置き場を作る。 */
export function fromTokenPair(pair: TokenPair, now: number = Date.now()): StoredSession {
  return {
    accessToken: pair.access_token,
    refreshToken: pair.refresh_token,
    lastActiveAt: now,
  };
}

/**
 * いまの通行証。保存先が使えるときは**毎回そちらを読む**（ほかのタブが取り直した新しい組を
 * すぐ使うため。古い組で取り直しに行くと、先に使われた控えとして断られてしまう）。
 */
export function getSession(): StoredSession | null {
  const stored = readStorage();
  if (stored !== undefined) {
    current = stored;
    return stored;
  }
  return current;
}

export function getAccessToken(): string | null {
  return getSession()?.accessToken ?? null;
}

export function setSession(session: StoredSession): void {
  current = session;
  writeStorage(session);
}

export function clearSession(): void {
  current = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 保存先が使えない設定。メモリ側は消えているので、そのまま進めてよい
  }
  try {
    // 以前の版はタブの中（sessionStorage）に置いていた。残っていれば一緒に消す
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // 同上
  }
}

/** 最後に操作した時刻を今にする（自動ログアウトの起点）。 */
export function touchSession(now: number = Date.now()): void {
  const session = getSession();
  if (!session) return;
  setSession({ ...session, lastActiveAt: now });
}

/** 画面を読み込み直したとき・新しいタブで開いたときに、保存先から復帰する。 */
export function restoreSession(): StoredSession | null {
  const stored = readStorage();
  if (stored) {
    current = stored;
    return stored;
  }
  // 以前の版（タブの中に置いていた）の写しがあれば、分け合う置き場へ移す
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      const parsed: unknown = JSON.parse(raw);
      if (isStoredSession(parsed)) {
        setSession(parsed);
        return parsed;
      }
    }
  } catch {
    // 読めなければ、ログインからやり直していただく
  }
  return current;
}

/**
 * ほかのタブで通行証が変わったときに呼ぶ（取り直した・ログアウトした・ログインした）。
 * 戻り値で聞くのをやめる。
 */
export function subscribeSession(listener: (session: StoredSession | null) => void): () => void {
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    listener(getSession());
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/**
 * 通行証に書かれた利用者ID（`sub`）を読む。読めなければ null（issue #394）。
 *
 * 署名の確かめはサーバの仕事なので、ここでは「どなたの通行証か」を読むだけ
 * （ほかのタブで別の方のログインに入れ替わったことに気づくために使う）。
 */
export function userIdOfAccessToken(token: string | null | undefined): string | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload: unknown = JSON.parse(window.atob(padded));
    if (payload !== null && typeof payload === 'object') {
      const sub = (payload as Record<string, unknown>).sub;
      if (typeof sub === 'string' && sub !== '') return sub;
    }
  } catch {
    // 形が違う通行証は読めないものとして扱う
  }
  return null;
}

/**
 * 保存先から読む。保存先が使えないときは undefined（メモリの控えを使う）、
 * 使えるが入っていないときは null。
 */
function readStorage(): StoredSession | null | undefined {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (!raw) {
    // 書けなかった（容量など）ときにメモリだけで持っている分は、そちらを使う
    return writeFailed ? undefined : null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isStoredSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** 保存先へ書けなかったか（書けなかったあいだは、メモリの控えを正にする）。 */
let writeFailed = false;

function writeStorage(session: StoredSession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    writeFailed = false;
  } catch {
    // 保存できない設定でも、メモリ側があるのでその画面では使い続けられる
    writeFailed = true;
  }
}

function isStoredSession(value: unknown): value is StoredSession {
  if (value === null || typeof value !== 'object') return false;
  const raw = value as Record<string, unknown>;
  return (
    typeof raw.accessToken === 'string' &&
    typeof raw.refreshToken === 'string' &&
    typeof raw.lastActiveAt === 'number'
  );
}
